import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Slide,
  Box,
  useTheme
} from '@mui/material';
import Close from 'assets/sort-icons/CloseIcon';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ModalWindow = ({
  open = false,
  onClose,
  title,
  children,
  actions,
  maxWidth = 'md',
  fullWidth = true,
  fullScreen = false,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  closeIcon = true,
  showBackdrop = true,
  backdropOpacity = 0.5,
  transition = 'slide',
  padding = 'normal',
  borderRadius = 'medium',
  className,
  sx = {},
  PaperProps = {},
  ContentProps = {}
}) => {
  const themeColor = useTheme();
  const handleClose = (event, reason) => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    if (disableEscapeKeyDown && reason === 'escapeKeyDown') {
      return;
    }
    if (onClose) {
      onClose(event, reason);
    }
  };

  const handleCloseButtonClick = (event) => {
    handleClose(event, 'closeButtonClick');
  };

  const getTransitionComponent = () => {
    switch (transition) {
      case 'none':
        return undefined;
      case 'slide':
      default:
        return Transition;
    }
  };

  const getPadding = () => {
    switch (padding) {
      case 'none': return 0;
      case 'small': return 2;
      case 'large': return 4;
      case 'normal':
      default: return 3;
    }
  };

  const getBorderRadius = () => {
    if (fullScreen) return 0;
    
    switch (borderRadius) {
      case 'none': return 0;
      case 'small': return 1;
      case 'large': return 3;
      case 'medium':
      default: return 2;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      // ВСЕГДА скролл внутри модалки - это правильно
      scroll="paper"
      TransitionComponent={getTransitionComponent()}
      TransitionProps={{ timeout: 300 }}
      BackdropProps={{
        sx: {
          backgroundColor: `rgba(0, 0, 0, ${backdropOpacity})`,
          ...(!showBackdrop && { display: 'none' })
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: theme => theme.spacing(getBorderRadius()),
          // Обеспечиваем скролл
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100% - 64px)', // Отступ от краев экрана
          ...PaperProps.sx,
          ...sx
        },
        ...PaperProps
      }}
      className={className}
    >
      {(title || closeIcon) && (
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: theme => theme.spacing(2, 3),
            borderBottom: title ? `1px solid ${themeColor.palette.rgd.cardBorder}` : 'none',
            flexShrink: 0 // Запрещаем сжиматься
          }}
        >
          {title && (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: {xs: '1.2rem', sm: '1.6rem', md: '1.76rem', lg: '1.76rem', xl: '2rem'}
              }}
            >
              {title}
            </Typography>
          )}
          
          {closeIcon && (
            <IconButton
              aria-label="close"
              onClick={handleCloseButtonClick}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8
              }}
            >
              <Close />
            </IconButton>
          )}
        </DialogTitle>
      )}

      {/* Ключевой момент: DialogContent должен скроллиться */}
      <DialogContent
        dividers={!title && actions}
        sx={{          
          padding: theme => theme.spacing(getPadding()),
          ...(title && {
            // Принудительно добавляем top padding когда есть заголовок
            '&.MuiDialogContent-root': {
              paddingTop: theme => theme.spacing(getPadding())
            }
          }),
          flex: 1,
          overflow: 'auto',
          ...(fullScreen && { padding: 0 }),
          ...ContentProps.sx
        }}
        {...ContentProps}
      >
        {children}
      </DialogContent>

      {actions && (
        <DialogActions
          sx={{
            padding: theme => theme.spacing(1.5, 3),
            borderTop: `1px solid ${themeColor.palette.rgd.cardBorder}`,
            flexShrink: 0 // Запрещаем сжиматься
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ModalWindow;